<template>
  <div class="receipts-panel">
    <v-layout row wrap align-center mb-3>
      <v-flex>
        <span class="subheading">Manage HPP receipt email subscriptions for this user</span>
      </v-flex>
      <v-flex shrink>
        <v-btn small color="primary" @click="openModal" :disabled="!userAccounts.length">
          <v-icon small left>fa-pencil</v-icon>
          Manage Receipts
        </v-btn>
      </v-flex>
    </v-layout>

    <v-alert v-if="!userAccounts.length" type="info" :value="true">
      User has no linked accounts. Link an account first to manage receipts.
    </v-alert>

    <v-data-table
      v-else
      :headers="tableHeaders"
      :items="receipts"
      :loading="isLoading"
      hide-actions
      class="elevation-1"
    >
      <template v-slot:items="props">
        <td>{{ props.item.account_id }}</td>
        <td>{{ props.item.account_name }}</td>
        <td>
          <v-chip small :color="props.item.receipt_enabled ? 'success' : 'grey lighten-1'" text-color="white">
            {{ props.item.receipt_enabled ? 'Enabled' : 'Disabled' }}
          </v-chip>
        </td>
      </template>
      <template v-slot:no-data>
        <v-alert :value="true" type="info">
          No receipt data available
        </v-alert>
      </template>
    </v-data-table>

    <!-- Manage Receipts Modal -->
    <v-dialog v-model="showModal" max-width="600px" persistent>
      <v-card>
        <v-card-title class="headline primary white--text">
          Manage HPP Receipts
          <v-spacer></v-spacer>
          <v-btn icon dark @click="closeModal">
            <v-icon>fa-times</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4">
          <p class="body-1 mb-3">
            Select which accounts should send HPP payment receipt copies to this user's email address.
          </p>

          <v-list>
            <v-list-tile v-for="item in modalItems" :key="item.account_id">
              <v-list-tile-action>
                <v-checkbox
                  v-model="item.enabled"
                  color="primary"
                  hide-details
                ></v-checkbox>
              </v-list-tile-action>
              <v-list-tile-content>
                <v-list-tile-title>{{ item.account_name }}</v-list-tile-title>
                <v-list-tile-sub-title>Account ID: {{ item.account_id }}</v-list-tile-sub-title>
              </v-list-tile-content>
            </v-list-tile>
          </v-list>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-btn flat @click="closeModal">Cancel</v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            :disabled="isProcessing"
            :loading="isProcessing"
            @click="saveReceipts"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Bus from '@/bus'
import ApiMixin from '@/mixins/api'

export default {
  name: 'ReceiptsPanel',
  mixins: [ApiMixin],

  props: {
    userId: {
      type: [Number, String],
      required: true
    },
    userAccounts: {
      type: Array,
      default: () => []
    }
  },

  data () {
    return {
      isLoading: false,
      isProcessing: false,
      showModal: false,
      receipts: [],
      modalItems: [],
      tableHeaders: [
        { text: 'ID', value: 'account_id', sortable: false },
        { text: 'Account', value: 'account_name', sortable: false },
        { text: 'Receipts', value: 'receipt_enabled', sortable: false }
      ]
    }
  },

  watch: {
    userId: {
      immediate: true,
      handler () {
        if (this.userId) {
          this.loadData()
        }
      }
    },
    userAccounts () {
      if (this.userId) {
        this.loadData()
      }
    }
  },

  methods: {
    async loadData () {
      this.isLoading = true
      try {
        let result = await this.api({
          method: 'get',
          url: `/users/${this.userId}/receipts`
        })
        this.receipts = result.data.receipts
      } catch (error) {
        console.error('Error loading receipts:', error)
      } finally {
        this.isLoading = false
      }
    },

    openModal () {
      // Create a working copy for the modal checkboxes
      this.modalItems = this.receipts.map(r => ({
        account_id: r.account_id,
        account_name: r.account_name,
        enabled: r.receipt_enabled
      }))
      this.showModal = true
    },

    closeModal () {
      this.showModal = false
      this.modalItems = []
    },

    async saveReceipts () {
      this.isProcessing = true
      try {
        await this.api({
          method: 'put',
          url: `/users/${this.userId}/receipts`,
          data: {
            accounts: this.modalItems.map(item => ({
              account_id: item.account_id,
              enabled: item.enabled
            }))
          }
        })

        Bus.$emit('openModal', {
          status: 'success',
          message: 'Receipt settings updated successfully'
        })

        this.closeModal()
        this.loadData()
      } catch (error) {
        Bus.$emit('openModal', {
          status: 'error',
          message: (error.response && error.response.data && error.response.data.error) || 'Error updating receipt settings'
        })
      } finally {
        this.isProcessing = false
      }
    }
  }
}
</script>

<style scoped>
.receipts-panel {
  padding: 8px;
}
</style>



// WEBPACK FOOTER //
// src/components/manage/user/ReceiptsPanel.vue